import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

/** Placeholder rendered for missing or unparseable values. */
export const EMPTY = "—";

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const isBlank = (value) => value === null || value === undefined || value === "";

/**
 * Parses `value` into a dayjs instance, converted to `tz` (IANA name, e.g. from
 * `useTimezone()`) when given. Date-only strings ("2024-03-15") are calendar dates,
 * not instants, so they are never shifted. Returns null when blank or invalid.
 */
const toDayjs = (value, tz) => {
  if (isBlank(value)) return null;
  const parsed = dayjs(value);
  if (!parsed.isValid()) return null;
  if (!tz || (typeof value === "string" && DATE_ONLY.test(value))) return parsed;
  return parsed.tz(tz);
};

const formatWith = (pattern) => (value, tz) => {
  const parsed = toDayjs(value, tz);
  return parsed ? parsed.format(pattern) : EMPTY;
};

/** "Mar 05, 2024". `tz` optional IANA timezone. */
export const formatDate = formatWith("MMM DD, YYYY");

/** "Mar 05, 2024 2:05 PM". `tz` optional IANA timezone. */
export const formatDateTime = formatWith("MMM DD, YYYY h:mm A");

/** "2:05 PM". `tz` optional IANA timezone. */
export const formatTime = formatWith("h:mm A");

const WEEKDAY_FORMATTERS = { short: formatWith("ddd"), long: formatWith("dddd") };

/** "Mon" (`style` "short") or "Monday" (`style` "long"). `tz` optional IANA timezone. */
export const formatWeekday = (value, style = "short", tz) =>
  (WEEKDAY_FORMATTERS[style] || WEEKDAY_FORMATTERS.short)(value, tz);

/** "Mar 2024". `tz` optional IANA timezone. */
export const formatMonth = formatWith("MMM YYYY");

/** "3 days ago" / "in 2 hours", relative to `base` (defaults to now). */
export const formatRelative = (value, base) => {
  const parsed = toDayjs(value);
  if (!parsed) return EMPTY;
  if (isBlank(base)) return parsed.fromNow();
  const baseParsed = dayjs(base);
  return baseParsed.isValid() ? parsed.from(baseParsed) : EMPTY;
};

const toNumber = (value) => {
  if (isBlank(value) || typeof value === "boolean") return null;
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : null;
};

/** "$1,234.50". `options` are Intl.NumberFormat overrides (e.g. `{ maximumFractionDigits: 0 }`). */
export const formatCurrency = (value, options = {}) => {
  const number = toNumber(value);
  if (number === null) return EMPTY;
  const { maximumFractionDigits: max, minimumFractionDigits: min } = options;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    ...options,
    // A max below USD's default min of 2 throws on older engines unless min is lowered too.
    ...(max !== undefined && min === undefined && { minimumFractionDigits: Math.min(2, max) }),
  }).format(number);
};

/** "1,234.5". `options` are Intl.NumberFormat overrides (e.g. `{ style: "percent" }`). */
export const formatNumber = (value, options) => {
  const number = toNumber(value);
  if (number === null) return EMPTY;
  return new Intl.NumberFormat("en-US", options).format(number);
};
